import React, { ReactNode } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { annotation } from '@cornerstonejs/tools';

import NavBar from '../NavBar';
import Svg from '../Svg';
import Icon from '../Icon';
import IconButton from '../IconButton';
import Dropdown from '../Dropdown';
import HeaderPatientInfo from '../HeaderPatientInfo';
import { PatientInfoVisibility } from '../../types/PatientInfoVisibility';

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  showPatientInfo = PatientInfoVisibility.VISIBLE_COLLAPSED,
  servicesManager,
  Secondary,
  appConfig,
  ...props
}: withAppTypes): ReactNode {
  const { t } = useTranslation('Header');

  // TODO: this should be passed in as a prop instead and the react-router-dom
  // dependency should be dropped
  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  return (
    <NavBar
      isSticky={isSticky}
      {...props}
    >
      <div className="relative h-[136px] items-center sm:h-[92px] lg:h-[48px]">
        <div className="absolute left-0 top-6 flex -translate-y-1/2 items-center lg:top-1/2">
          <div
            className={classNames(
              'mr-3 inline-flex items-center',
              isReturnEnabled && 'cursor-pointer'
            )}
            onClick={onClickReturn}
            data-cy="return-to-work-list"
          >
            {isReturnEnabled && (
              <Icon
                name="chevron-left"
                className="text-primary-active w-8"
              />
            )}
            <div className="ml-1"></div>
          </div>
        </div>
        <div className="absolute top-1/2 left-[250px] hidden h-8 -translate-y-1/2 lg:block">
          {Secondary}
        </div>
        <div className="absolute top-12 right-0 bottom-1 left-0 flex items-center px-1 lg:top-1/2 lg:right-auto lg:bottom-auto lg:left-1/2 lg:h-auto lg:-translate-x-1/2 lg:-translate-y-1/2 lg:transform lg:px-0">
          <div className="flex w-full items-center justify-evenly lg:w-max">{children}</div>
        </div>
        <div className="absolute top-6 right-0 left-10 flex min-w-0 -translate-y-1/2 select-none items-center justify-end lg:top-1/2 lg:left-auto">
          {showPatientInfo !== PatientInfoVisibility.DISABLED && (
            <div className="min-w-0 overflow-hidden">
              <HeaderPatientInfo
                servicesManager={servicesManager}
                appConfig={appConfig}
              />
            </div>
          )}
          <div className="border-primary-dark mx-1.5 h-[25px] border-r"></div>
          <div className="flex-shrink-0">
            <Dropdown
              id="options"
              showDropdownIcon={false}
              list={menuOptions}
              alignment="right"
            >
              <IconButton
                id={'options-settings-icon'}
                variant="text"
                color="inherit"
                size="initial"
                className="text-common-light hover:bg-secondary-main h-full w-full rounded p-1 transition-colors hover:text-white"
              >
                <Icon name="icon-settings" />
              </IconButton>
            </Dropdown>
          </div>
        </div>
      </div>
    </NavBar>
  );
}

Header.propTypes = {
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    })
  ),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  isReturnEnabled: PropTypes.bool,
  isSticky: PropTypes.bool,
  onClickReturnButton: PropTypes.func,
  showPatientInfo: PropTypes.string,
  servicesManager: PropTypes.object,
};

export default Header;
